/**
 * RetailerOffers orchestrator — web-layer glue.
 *
 * Deferred from T6; picked up in T8.3.
 *
 * RetailerOffers are browsed read-only for the offer feed (TanStack Query hooks
 * handle that directly — see `useRetailerOffers`). The write flows
 * that need orchestration are:
 *
 *   1. `saveToHouseholdProducts` — the user picks a RetailerOffer and adds it to their
 *      HouseholdProducts catalog. Legacy did this as a single atomic backend call;
 *      locally we follow the T6 pattern:
 *
 *        (1) reducer — optimistically append the HouseholdProductWithDetails row
 *        (2) storage.putProduct — persist the base row
 *        (3) fetch.post — call the backend; on success swap id + merge
 *
 *   T12.1.2 removed the offer CRUD wrappers (create / update / delete) — they
 *   were dead (zero non-test callers). Offer writes flow through the sync
 *   engine's `upsertOfferPricing`, not a user-facing CRUD path.
 *
 * Architecture contract invariant I4 (offer browsing scoped to household-
 * selected StoreLocations) is enforced at the READ layer — callers pass a
 * storeId the household has selected; the orchestrator never touches the
 * selection. If a UI queries offers for a store that isn't in the household
 * selection, the backend already rejects (or returns empty per household
 * scoping). No orchestrator work needed here — documented so the next agent
 * doesn't re-debate it.
 */

import type { QueryClient } from '@tanstack/react-query';
import { keys } from '@shopista/shared/api/hooks';
import {
  addProduct,
  removeProduct,
  updateProduct,
  type Adapters,
} from '@shopista/shared/stores';
import type {
  HouseholdProduct,
  HouseholdProductWithDetails,
  SaveOfferToHouseholdProductsRequest,
} from '@shopista/shared/types';
import type { RootState, RootStore } from './root-store';

export interface RetailerOffersOrchestrator {
  /**
   * Pick a RetailerOffer, save it as a new HouseholdProduct, and link the two
   * server-side. Follows the T6 reducer -> storage -> API contract because
   * it mutates the HouseholdProducts slice.
   */
  handleSaveOfferToHouseholdProducts(input: {
    optimistic: HouseholdProductWithDetails;
    request: SaveOfferToHouseholdProductsRequest;
  }): Promise<HouseholdProductWithDetails>;
}

export function createRetailerOffersOrchestrator(
  store: RootStore,
  adapters: Adapters,
  queryClient?: QueryClient
): RetailerOffersOrchestrator {
  function withSlice<T>(fn: (state: RootState) => T): T {
    return fn(store.getState());
  }

  function invalidateHouseholdProducts(): void {
    if (!queryClient) return;
    void queryClient.invalidateQueries({ queryKey: keys.HouseholdProducts.all });
  }

  return {
    async handleSaveOfferToHouseholdProducts({ optimistic, request }) {
      // (1) pure reducer — treat this as "add a new HouseholdProduct" locally.
      const current = withSlice((s) => s.HouseholdProducts);
      const next = addProduct(current, { product: optimistic });
      // (2) commit
      store.setState({ HouseholdProducts: next });
      // (3) persist
      await adapters.storage.putProduct(stripDetails(optimistic));
      // (4) server
      try {
        const response = await adapters.api.RetailerOffers.saveToHouseholdProducts(
          request
        );
        const serverProduct = response.myProduct;
        // Reconcile id (server will almost always assign a new one since the
        // optimistic id was client-side).
        if (serverProduct.id !== optimistic.id) {
          const after = withSlice((s) => s.HouseholdProducts);
          const afterRemove = removeProduct(after, { productId: optimistic.id });
          const afterAdd = addProduct(afterRemove, { product: serverProduct });
          store.setState({ HouseholdProducts: afterAdd });
          await adapters.storage.deleteProduct(optimistic.id);
          await adapters.storage.putProduct(stripDetails(serverProduct));
        } else {
          const after = withSlice((s) => s.HouseholdProducts);
          store.setState({
            HouseholdProducts: updateProduct(after, {
              productId: serverProduct.id,
              changes: serverProduct,
            }),
          });
          await adapters.storage.putProduct(stripDetails(serverProduct));
        }
        invalidateHouseholdProducts();
        return serverProduct;
      } catch (err) {
        const after = withSlice((s) => s.HouseholdProducts);
        store.setState({
          HouseholdProducts: removeProduct(after, { productId: optimistic.id }),
        });
        await adapters.storage
          .deleteProduct(optimistic.id)
          .catch(() => {});
        throw err;
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripDetails(
  row: HouseholdProductWithDetails | Partial<HouseholdProductWithDetails>
): HouseholdProduct {
  const {
    category_name: _cn,
    linked_offer_count: _loc,
    favorite_offer_count: _foc,
    default_offer_id: _doid,
    default_offer_title: _dot,
    default_offer_price: _dop,
    default_store_name: _dsn,
    ...base
  } = row as HouseholdProductWithDetails;
  return base as HouseholdProduct;
}
